// src/components/CreateDriver.js
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import axios from "../axiosConfig";
import "../styles/CreateDriver.css";
import Cropper from "react-easy-crop";
import { FaUpload, FaFileAlt, FaPencilAlt, FaCalendarAlt } from "react-icons/fa";
import { FiRefreshCw } from "react-icons/fi";
import getCroppedImg from "../components/cropImage.js";
import { customSelectStylesFlagLeft } from "../styles/reactSelectStyles";

/* ===== Countries (mesmo formato do CreatePlace) ===== */
const countryOptions = [
  { label: "Austria", value: "Austria", code: "at" },
  { label: "Belgium", value: "Belgium", code: "be" },
  { label: "Croatia", value: "Croatia", code: "hr" },
  { label: "Czech Republic", value: "Czech Republic", code: "cz" },
  { label: "Denmark", value: "Denmark", code: "dk" },
  { label: "Estonia", value: "Estonia", code: "ee" },
  { label: "Finland", value: "Finland", code: "fi" },
  { label: "France", value: "France", code: "fr" },
  { label: "Germany", value: "Germany", code: "de" },
  { label: "Hungary", value: "Hungary", code: "hu" },
  { label: "Italy", value: "Italy", code: "it" },
  { label: "Netherlands", value: "Netherlands", code: "nl" },
  { label: "Norway", value: "Norway", code: "no" },
  { label: "Poland", value: "Poland", code: "pl" },
  { label: "Portugal", value: "Portugal", code: "pt" },
  { label: "Spain", value: "Spain", code: "es" },
  { label: "Sweden", value: "Sweden", code: "se" },
  { label: "Switzerland", value: "Switzerland", code: "ch" },
  { label: "United Kingdom", value: "United Kingdom", code: "gb" },
];

/* prefixos de telefone por código ISO (para montar +contact_number) */
const dialByCode = {
  at: "43", be: "32", hr: "385", cz: "420", dk: "45", ee: "372", fi: "358", fr: "33",
  de: "49", hu: "36", it: "39", nl: "31", no: "47", pl: "48", pt: "351", es: "34",
  se: "46", ch: "41", gb: "44",
};

/* ===== Especificação de telefone por país (máscara/placeholder/limite) ===== */
const phoneSpecByCode = {
  pt: { digits: 9, mask: [3, 3, 3], example: "912 345 678" },
  es: { digits: 9, mask: [3, 3, 3], example: "612 345 678" },
  fr: { digits: 9, mask: [1, 2, 2, 2, 2], example: "6 12 34 56 78" },
  it: { digits: 10, mask: [3, 3, 4], example: "312 345 6789" },
  de: { digits: 11, mask: [3, 4, 4], example: "151 2345 6789" },
  nl: { digits: 9, mask: [2, 3, 4], example: "06 123 4567" },
  gb: { digits: 10, mask: [4, 3, 3], example: "0712 345 678" },
  ch: { digits: 9, mask: [2, 3, 2, 2], example: "78 123 45 67" },
  se: { digits: 9, mask: [3, 2, 2, 2], example: "070 12 34 56" },
};
const defaultPhoneSpec = { digits: 9, mask: [3, 3, 3], example: "123 456 789" };

const onlyDigits = (s = "") => s.replace(/\D+/g, "");
function formatWithMask(digits, mask) {
  const parts = [];
  let idx = 0;
  for (const size of mask) {
    if (idx >= digits.length) break;
    parts.push(digits.slice(idx, idx + size));
    idx += size;
  }
  if (idx < digits.length) parts.push(digits.slice(idx));
  return parts.filter(Boolean).join(" ");
}

function CreateDriver() {
  const navigate = useNavigate();

  const [driver, setDriver] = useState({
    name: "",
    date_of_birth: "",
    driver_id: "",
    DriverIDExpiry: "",
    driver_license: "",
    DriverLicenseExpiry: "",
    driver_tacho_number: "",
    id_number: "",
    currently_assigned_truck: "",
    currently_assigned_trailer: "",
    driver_id_photo: null,
    profile_photo: null,
    driver_license_photo: null,
    driver_tacho_card_photo: null,
  });

  const [selectedPhoneCountry, setSelectedPhoneCountry] =
    useState(countryOptions.find((c) => c.code === "pt") || countryOptions[0]);

  // Telefone controlado: dígitos "raw" + versão com espaços (UI)
  const [phoneRaw, setPhoneRaw] = useState("");
  const [phoneDisplay, setPhoneDisplay] = useState("");

  const [invalidFields, setInvalidFields] = useState({});
  const [errorMessage, setErrorMessage] = useState("");

  // Cropper
  const [showCropper, setShowCropper] = useState(false);
  const [rawImage, setRawImage] = useState(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  /* ===== Handlers ===== */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setDriver((p) => ({ ...p, [name]: value }));
    if (invalidFields[name]) {
      setInvalidFields((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    const file = files?.[0];
    if (!file) return;

    setDriver((p) => ({ ...p, [name]: file }));
    if (name === "profile_photo") {
      const reader = new FileReader();
      reader.onload = () => {
        setRawImage(reader.result);
        setShowCropper(true);
      };
      reader.readAsDataURL(file);
    }
    if (invalidFields[name]) {
      setInvalidFields((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleCropConfirm = async () => {
    if (!rawImage || !croppedAreaPixels) return;
    const croppedBlob = await getCroppedImg(rawImage, croppedAreaPixels, 120);
    setProfilePhotoPreview(URL.createObjectURL(croppedBlob));
    setShowCropper(false);
    setDriver((prev) => ({ ...prev, profile_photo: croppedBlob }));
  };

  const handleCountryPhoneChange = (opt) => {
    setSelectedPhoneCountry(opt);
    if (invalidFields.contact_number) {
      setInvalidFields((prev) => ({ ...prev, contact_number: undefined }));
    }
    // reformatar input existente com a máscara do novo país
    const spec = phoneSpecByCode[opt?.code] || defaultPhoneSpec;
    setPhoneDisplay(formatWithMask(phoneRaw, spec.mask));
  };

  const handlePhoneNumberChange = (e) => {
    const code = selectedPhoneCountry?.code || "pt";
    const spec = phoneSpecByCode[code] || defaultPhoneSpec;

    const digits = onlyDigits(e.target.value).slice(0, spec.digits);
    setPhoneRaw(digits);
    setPhoneDisplay(formatWithMask(digits, spec.mask));

    if (invalidFields.contact_number) {
      setInvalidFields((prev) => ({ ...prev, contact_number: undefined }));
    }
  };

  /* ===== Submit ===== */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    const errs = {};
    if (!driver.name.trim()) errs.name = "Name is required.";

    const codeIso = selectedPhoneCountry?.code || "pt";
    const spec = phoneSpecByCode[codeIso] || defaultPhoneSpec;
    if (phoneRaw.length !== spec.digits) {
      errs.contact_number = `Invalid phone. Expect ${spec.digits} digits for ${selectedPhoneCountry?.label || "selected country"}.`;
    }

    if (!driver.date_of_birth) errs.date_of_birth = "Date of birth is required.";
    if (!driver.driver_id.trim()) errs.driver_id = "Driver ID is required.";
    if (!driver.DriverIDExpiry) errs.DriverIDExpiry = "Expiry date is required.";
    if (!driver.driver_license.trim()) errs.driver_license = "Driver license is required.";
    if (!driver.DriverLicenseExpiry) errs.DriverLicenseExpiry = "Expiry date is required.";
    if (!driver.driver_tacho_number.trim()) errs.driver_tacho_number = "Tacho number is required.";
    if (!driver.id_number.trim()) errs.id_number = "ID Number is required.";
    if (!driver.driver_id_photo) errs.driver_id_photo = "Driver ID file is required.";
    if (!driver.driver_license_photo) errs.driver_license_photo = "License photo is required.";
    if (!driver.driver_tacho_card_photo) errs.driver_tacho_card_photo = "Tacho card photo is required.";

    setInvalidFields(errs);
    if (Object.keys(errs).length) return;

    const dial = dialByCode[codeIso] || "351";
    const formData = new FormData();
    formData.append("name", driver.name);
    formData.append("contact_number", `+${dial}${phoneRaw}`);
    formData.append("date_of_birth", driver.date_of_birth);
    formData.append("driver_id", driver.driver_id);
    formData.append("DriverIDExpiry", driver.DriverIDExpiry);
    formData.append("driver_license", driver.driver_license);
    formData.append("DriverLicenseExpiry", driver.DriverLicenseExpiry);
    formData.append("driver_tacho_number", driver.driver_tacho_number);
    formData.append("id_number", driver.id_number);
    formData.append("currently_assigned_truck", driver.currently_assigned_truck);
    formData.append("currently_assigned_trailer", driver.currently_assigned_trailer);
    formData.append("profile_photo", driver.profile_photo);
    formData.append("driver_id_photo", driver.driver_id_photo);
    formData.append("driver_license_photo", driver.driver_license_photo);
    formData.append("driver_tacho_card_photo", driver.driver_tacho_card_photo);

    try {
      const resp = await axios.post("/drivers", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (resp.status === 200 || resp.status === 201) {
        navigate("/drivers");
      } else {
        setErrorMessage("Unexpected response. Please try again.");
      }
    } catch (err) {
      setErrorMessage(
        err.response?.data?.error || "Failed to create driver. Please try again."
      );
    }
  };

  const phonePlaceholder =
    (phoneSpecByCode[selectedPhoneCountry?.code || "pt"] || defaultPhoneSpec).example;

  return (
    <div className="cd-container">
      <div className="cd-back-row">
        <button type="button" className="cd-back" onClick={() => navigate("/drivers")}>
          &larr; Back
        </button>
      </div>

      <div className="cd-content">
        <h1 className="cd-title">CREATE DRIVER</h1>
        {errorMessage && <p className="cd-msg-error">{errorMessage}</p>}

        <form onSubmit={handleSubmit} className="cd-form">
          {/* ===== Basic Information ===== */}
          <h2 className="cd-section-title">Basic Information</h2>
          <div className="cd-basic">
            {/* Foto à esquerda */}
            <div className="cd-profile">
              <div
                className="cd-profile-circle"
                onClick={() => document.getElementById("profile_photo").click()}
              >
                {profilePhotoPreview ? (
                  <img src={profilePhotoPreview} alt="Profile" className="cd-profile-img" />
                ) : (
                  <span className="cd-profile-ph">
                    <FaUpload />
                    <span>Choose Photo</span>
                  </span>
                )}
                <input
                  id="profile_photo"
                  type="file"
                  accept=".jpeg,.jpg,.png"
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                  name="profile_photo"
                />
              </div>
              {profilePhotoPreview && (
                <div className="cd-profile-actions">
                  <button
                    type="button"
                    className="cd-icon-btn"
                    title="Change photo"
                    onClick={() => document.getElementById("profile_photo").click()}
                  >
                    <FiRefreshCw />
                  </button>
                  <button
                    type="button"
                    className="cd-icon-btn"
                    title="Edit (crop/zoom)"
                    onClick={() => setShowCropper(true)}
                  >
                    <FaPencilAlt />
                  </button>
                </div>
              )}
            </div>

            {/* Campos à direita (Name, Contact, DOB) */}
            <div className="cd-basic-fields">
              <div className="cd-input-group">
                <label className="cd-label" htmlFor="name">Name*</label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  value={driver.name}
                  onChange={handleChange}
                  className={invalidFields.name ? "cd-input-error" : ""}
                  placeholder="Enter driver name"
                />
                <span className="cd-field-error">
                  {invalidFields.name ? "Name is required." : "\u00A0"}
                </span>
              </div>

              <div className="cd-input-group">
                <label className="cd-label">Contact Phone*</label>
                <div className={`cd-phone ${invalidFields.contact_number ? "cd-phone-error" : ""}`}>
                  <div className="cd-flag">
                    <Select
                      options={countryOptions}
                      value={selectedPhoneCountry}
                      onChange={handleCountryPhoneChange}
                      styles={customSelectStylesFlagLeft}
                      classNamePrefix="Select"
                      formatOptionLabel={(country, { context }) =>
                        context === "value" ? (
                          <img
                            src={`https://flagcdn.com/w40/${country.code}.png`}
                            alt={country.label}
                            style={{ width: 20, height: 15, objectFit: "cover", borderRadius: 2 }}
                            onError={(e) => (e.currentTarget.style.display = "none")}
                          />
                        ) : (
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <img
                              src={`https://flagcdn.com/w40/${country.code}.png`}
                              alt={country.label}
                              style={{ width: 20, height: 15, objectFit: "cover", borderRadius: 2 }}
                              onError={(e) => (e.currentTarget.style.display = "none")}
                            />
                            <span>{country.label}</span>
                          </div>
                        )
                      }
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      menuPlacement="auto"
                    />
                  </div>

                  <div className="cd-phone-prefix">
                    +{dialByCode[selectedPhoneCountry?.code || "pt"] || "351"}
                  </div>

                  <div className="cd-phone-input">
                    <input
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      placeholder={phonePlaceholder}
                      value={phoneDisplay}
                      onChange={handlePhoneNumberChange}
                      onPaste={(ev) => {
                        ev.preventDefault();
                        const pasted = (ev.clipboardData.getData("text") || "").toString();
                        const code = selectedPhoneCountry?.code || "pt";
                        const spec = phoneSpecByCode[code] || defaultPhoneSpec;
                        const digits = onlyDigits(pasted).slice(0, spec.digits);
                        setPhoneRaw(digits);
                        setPhoneDisplay(formatWithMask(digits, spec.mask));
                      }}
                    />
                  </div>
                </div>
                <span className="cd-field-error-phone">
                  {invalidFields.contact_number ? invalidFields.contact_number : "\u00A0"}
                </span>
              </div>

              <div className="cd-input-group">
                <label className="cd-label" htmlFor="date_of_birth">Date of Birth*</label>
                <div className="cd-date">
                  <input
                    id="date_of_birth"
                    name="date_of_birth"
                    type="date"
                    value={driver.date_of_birth}
                    onChange={handleChange}
                    className={invalidFields.date_of_birth ? "cd-input-error" : ""}
                  />
                  <FaCalendarAlt
                    className="cd-date-icon"
                    onClick={() => {
                      const el = document.getElementById("date_of_birth");
                      if (!el) return;
                      el.focus();
                      if (typeof el.showPicker === "function") el.showPicker();
                    }}
                  />
                </div>
                <span className="cd-field-error">
                  {invalidFields.date_of_birth ? "Date of birth is required." : "\u00A0"}
                </span>
              </div>
            </div>
          </div>

          {/* ===== Identification ===== */}
          <h2 className="cd-section-title">Identification</h2>

          {/* Linha com 4 colunas, na ordem pedida */}
          <div className="cd-row-4">
            <div className="cd-input-group">
              <label className="cd-label" htmlFor="driver_id">Driver ID*</label>
              <input
                id="driver_id"
                name="driver_id"
                type="text"
                value={driver.driver_id}
                onChange={handleChange}
                className={invalidFields.driver_id ? "cd-input-error" : ""}
                placeholder="Enter driver id"
              />
              <span className="cd-field-error">
                {invalidFields.driver_id ? "Driver ID is required." : "\u00A0"}
              </span>
            </div>

            <div className="cd-input-group">
              <label className="cd-label" htmlFor="DriverIDExpiry">Expiry Date (Driver ID)*</label>
              <div className="cd-date">
                <input
                  id="DriverIDExpiry"
                  name="DriverIDExpiry"
                  type="date"
                  value={driver.DriverIDExpiry}
                  onChange={handleChange}
                  className={invalidFields.DriverIDExpiry ? "cd-input-error" : ""}
                />
                <FaCalendarAlt
                  className="cd-date-icon"
                  onClick={() => {
                    const el = document.getElementById("DriverIDExpiry");
                    if (!el) return;
                    el.focus();
                    if (typeof el.showPicker === "function") el.showPicker();
                  }}
                />
              </div>
              <span className="cd-field-error">
                {invalidFields.DriverIDExpiry ? "Expiry date is required." : "\u00A0"}
              </span>
            </div>

            <div className="cd-input-group">
              <label className="cd-label" htmlFor="driver_license">Driver License*</label>
              <input
                id="driver_license"
                name="driver_license"
                type="text"
                value={driver.driver_license}
                onChange={handleChange}
                className={invalidFields.driver_license ? "cd-input-error" : ""}
                placeholder="Enter driver license"
              />
              <span className="cd-field-error">
                {invalidFields.driver_license ? "Driver license is required." : "\u00A0"}
              </span>
            </div>

            <div className="cd-input-group">
              <label className="cd-label" htmlFor="DriverLicenseExpiry">Expiry Date (Driver License)*</label>
              <div className="cd-date">
                <input
                  id="DriverLicenseExpiry"
                  name="DriverLicenseExpiry"
                  type="date"
                  value={driver.DriverLicenseExpiry}
                  onChange={handleChange}
                  className={invalidFields.DriverLicenseExpiry ? "cd-input-error" : ""}
                />
                <FaCalendarAlt
                  className="cd-date-icon"
                  onClick={() => {
                    const el = document.getElementById("DriverLicenseExpiry");
                    if (!el) return;
                    el.focus();
                    if (typeof el.showPicker === "function") el.showPicker();
                  }}
                />
              </div>
              <span className="cd-field-error">
                {invalidFields.DriverLicenseExpiry ? "Expiry date is required." : "\u00A0"}
              </span>
            </div>
          </div>

          {/* 2 colunas: Tacho Number, ID Number */}
          <div className="cd-row-2">
            <div className="cd-input-group">
              <label className="cd-label" htmlFor="driver_tacho_number">Tacho Number*</label>
              <input
                id="driver_tacho_number"
                name="driver_tacho_number"
                type="text"
                value={driver.driver_tacho_number}
                onChange={handleChange}
                className={invalidFields.driver_tacho_number ? "cd-input-error" : ""}
                placeholder="Enter tacho number"
              />
              <span className="cd-field-error">
                {invalidFields.driver_tacho_number ? "Tacho number is required." : "\u00A0"}
              </span>
            </div>

            <div className="cd-input-group">
              <label className="cd-label" htmlFor="id_number">ID Number*</label>
              <input
                id="id_number"
                name="id_number"
                type="text"
                value={driver.id_number}
                onChange={handleChange}
                className={invalidFields.id_number ? "cd-input-error" : ""}
                placeholder="Enter id number"
              />
              <span className="cd-field-error">
                {invalidFields.id_number ? "ID Number is required." : "\u00A0"}
              </span>
            </div>
          </div>

          {/* 2 colunas: Truck, Trailer */}
          <div className="cd-row-2">
            <div className="cd-input-group">
              <label className="cd-label" htmlFor="currently_assigned_truck">Currently Assigned Truck</label>
              <input
                id="currently_assigned_truck"
                name="currently_assigned_truck"
                type="text"
                value={driver.currently_assigned_truck}
                onChange={handleChange}
                placeholder="Enter currently assigned truck"
              />
              <span className="cd-field-error">{"\u00A0"}</span>
            </div>

            <div className="cd-input-group">
              <label className="cd-label" htmlFor="currently_assigned_trailer">Currently Assigned Trailer</label>
              <input
                id="currently_assigned_trailer"
                name="currently_assigned_trailer"
                type="text"
                value={driver.currently_assigned_trailer}
                onChange={handleChange}
                placeholder="Enter currently assigned trailer"
              />
              <span className="cd-field-error">{"\u00A0"}</span>
            </div>
          </div>

          {/* ===== Upload Documents (3 colunas) ===== */}
          <h2 className="cd-section-title">Upload Documents</h2>
          <div className="cd-row-3">
            {/* Driver ID Photo */}
            <div className="cd-input-group">
              <label className="cd-label">Driver ID Photo*</label>
              <div
                className={`cd-upload ${invalidFields.driver_id_photo ? "cd-input-error" : ""}`}
                onClick={() => document.getElementById("driver_id_photo").click()}
                role="button"
              >
                {driver.driver_id_photo ? (
                  <div className="cd-file-row">
                    <div className="cd-file-info" title={driver.driver_id_photo.name}>
                      <FaFileAlt className="cd-file-icon" />
                      <span className="cd-file-name">{driver.driver_id_photo.name}</span>
                    </div>
                    <button
                      type="button"
                      className="cd-file-change"
                      onClick={(e) => {
                        e.stopPropagation();
                        document.getElementById("driver_id_photo").click();
                      }}
                    >
                      Change
                    </button>
                  </div>
                ) : (
                  <div className="cd-upload-ph">
                    <FaUpload />
                    <span>Upload your file here.</span>
                  </div>
                )}
                <input
                  id="driver_id_photo"
                  name="driver_id_photo"
                  type="file"
                  accept=".jpeg,.jpg,.png,.pdf"
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />
              </div>
              <span className="cd-field-error-file">
                {invalidFields.driver_id_photo ? invalidFields.driver_id_photo : "\u00A0"}
              </span>
            </div>

            {/* Driver License Photo */}
            <div className="cd-input-group">
              <label className="cd-label">Driver License Photo*</label>
              <div
                className={`cd-upload ${invalidFields.driver_license_photo ? "cd-input-error" : ""}`}
                onClick={() => document.getElementById("driver_license_photo").click()}
                role="button"
              >
                {driver.driver_license_photo ? (
                  <div className="cd-file-row">
                    <div className="cd-file-info" title={driver.driver_license_photo.name}>
                      <FaFileAlt className="cd-file-icon" />
                      <span className="cd-file-name">{driver.driver_license_photo.name}</span>
                    </div>
                    <button
                      type="button"
                      className="cd-file-change"
                      onClick={(e) => {
                        e.stopPropagation();
                        document.getElementById("driver_license_photo").click();
                      }}
                    >
                      Change
                    </button>
                  </div>
                ) : (
                  <div className="cd-upload-ph">
                    <FaUpload />
                    <span>Upload your file here.</span>
                  </div>
                )}
                <input
                  id="driver_license_photo"
                  name="driver_license_photo"
                  type="file"
                  accept=".jpeg,.jpg,.png,.pdf"
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />
              </div>
              <span className="cd-field-error-file">
                {invalidFields.driver_license_photo ? invalidFields.driver_license_photo : "\u00A0"}
              </span>
            </div>

            {/* Tacho Card Photo */}
            <div className="cd-input-group">
              <label className="cd-label">Tacho Card Photo*</label>
              <div
                className={`cd-upload ${invalidFields.driver_tacho_card_photo ? "cd-input-error" : ""}`}
                onClick={() => document.getElementById("driver_tacho_card_photo").click()}
                role="button"
              >
                {driver.driver_tacho_card_photo ? (
                  <div className="cd-file-row">
                    <div className="cd-file-info" title={driver.driver_tacho_card_photo.name}>
                      <FaFileAlt className="cd-file-icon" />
                      <span className="cd-file-name">{driver.driver_tacho_card_photo.name}</span>
                    </div>
                    <button
                      type="button"
                      className="cd-file-change"
                      onClick={(e) => {
                        e.stopPropagation();
                        document.getElementById("driver_tacho_card_photo").click();
                      }}
                    >
                      Change
                    </button>
                  </div>
                ) : (
                  <div className="cd-upload-ph">
                    <FaUpload />
                    <span>Upload your file here.</span>
                  </div>
                )}
                <input
                  id="driver_tacho_card_photo"
                  name="driver_tacho_card_photo"
                  type="file"
                  accept=".jpeg,.jpg,.png,.pdf"
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />
              </div>
              <span className="cd-field-error-file">
                {invalidFields.driver_tacho_card_photo ? invalidFields.driver_tacho_card_photo : "\u00A0"}
              </span>
            </div>
          </div>

          {/* Botões */}
          <div className="cd-buttons">
            <button
              type="button"
              className="cd-btn-cancel"
              onClick={() => navigate("/drivers")}
            >
              Cancel
            </button>
            <button type="submit" className="cd-btn-submit">
              Create Driver
            </button>
          </div>
        </form>
      </div>

      {/* ===== Cropper Modal ===== */}
      {showCropper && (
        <div className="cd-cropper">
          <div className="cd-cropper-bg" onClick={() => setShowCropper(false)} />
          <div className="cd-cropper-content">
            <div className="cd-cropper-area">
              <Cropper
                image={rawImage}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={(_, croppedPixels) => setCroppedAreaPixels(croppedPixels)}
              />
            </div>

            <input
              type="range"
              min={1}
              max={3}
              step={0.01}
              value={zoom}
              onChange={(e) => setZoom(e.target.value)}
              style={{ width: "72%", margin: "12px 0 6px" }}
            />

            <div className="cd-cropper-actions">
              <button type="button" className="cd-btn-submit" onClick={handleCropConfirm}>
                Confirm
              </button>
              <button type="button" className="cd-btn-cancel" onClick={() => setShowCropper(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CreateDriver;
